/**
 * @typedef { import('./types').ScrollDirectionEvent} ScrollDirectionEvent
 * @typedef { import('focus-trap').FocusTrap } FocusTrap
 * @typedef {HTMLElement & { trap: FocusTrap }} NavPage
 */

if (!customElements.get('header-global')) {
  customElements.define(
    'header-global',
    class GlobalHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.pinHeaderOnScrollDirectionChange();
        this.mountScrollDirectionListener();

        this.addEventListener('click', (e) => {
          if (e.target instanceof Element && e.target.closest('[data-tab-id="wishlist"]')) {
            window.gtag('event', 'wishlist_action', {
              interaction_type: 'click',
              element_type: 'header_toggle',
              page_path_clean: window.location.pathname.replace(/\/$/, ''),
            });
          }
        });
      }

      pinHeaderOnScrollDirectionChange() {
        const header = this.closest('header.header-group');

        if (!header || !(header instanceof HTMLElement)) return;

        window.addEventListener('scrolldirection', (/** @type {ScrollDirectionEvent} */ e) => {
          const { direction, y } = e.detail;

          if (header.querySelector('[aria-expanded="true"]')) return;

          if (direction === 'up') {
            header.dataset.revealed = 'true';
            header.dataset.scrolled = String(y > 0);
          } else if (direction === 'down') {
            header.dataset.revealed = 'false';
            header.dataset.scrolled = 'false';
          } else if (direction === 'initial') {
            header.dataset.revealed = 'true';

            if (y > 0) {
              header.dataset.scrolled = 'true';
            } else {
              header.dataset.scrolled = 'false';
            }
          }
        });
      }

      /**
       * @param {'up' | 'down' | 'initial'} scrollDirection - The direction of the scroll
       * @param {number} scrollY - The y position of the scroll
       */
      dispatchScrollDirectionEvent(scrollDirection, scrollY) {
        const scrollDirectionEvent = new CustomEvent('scrolldirection', {
          detail: {
            direction: scrollDirection,
            y: scrollY,
          },
        });

        window.dispatchEvent(scrollDirectionEvent);
      }

      mountScrollDirectionListener() {
        let currentY = window.scrollY;
        let lastDirection = 'initial';
        let lastTriggerY = currentY;

        this.dispatchScrollDirectionEvent('initial', currentY);

        window.addEventListener(
          'scroll',
          window.throttle(() => {
            const newY = window.scrollY;
            const buffer = 50; // Buffer to reduce sensitivity of the scroll direction event

            if (newY === 0) {
              lastDirection = 'up';
              lastTriggerY = newY;
              return this.dispatchScrollDirectionEvent('up', newY);
            }

            if (newY > currentY) {
              if (lastDirection !== 'down' && newY - lastTriggerY >= buffer) {
                lastDirection = 'down';
                lastTriggerY = newY;
                this.dispatchScrollDirectionEvent('down', newY);
              } else if (lastDirection === 'down') {
                lastTriggerY = newY;
              }
            } else if (newY < currentY) {
              if (lastDirection !== 'up' && lastTriggerY - newY >= buffer) {
                lastDirection = 'up';
                lastTriggerY = newY;
                this.dispatchScrollDirectionEvent('up', newY);
              } else if (lastDirection === 'up') {
                lastTriggerY = newY;
              }
            }

            currentY = newY;
          }, 0),
          { passive: true },
        );
      }
    },
  );
}

if (!customElements.get('header-tray-menu')) {
  customElements.define(
    'header-tray-menu',
    class HeaderTrayMenu extends HTMLElement {
      constructor() {
        super();

        /** @type {NavPage[]} */
        this.pages = Array.from(this.querySelectorAll('[data-tray-page]'));
      }

      connectedCallback() {
        /** @type {HTMLElement | null} */
        const header = this.closest('header-global');

        const pages = this.pages;

        pages.forEach((page) => {
          /** @type {HTMLElement | null} - Used exclusively for the fallback focus when navigating backwards through the menu tree */
          const close = page.querySelector('[data-tray-close]');

          /** @type { HTMLAnchorElement | HTMLButtonElement | null } */
          const initial = page.querySelector('.section-header-tray-menu-link');

          const scheme = header?.dataset.trayScheme || 'scheme-2';

          window.traps.create(page, {
            onActivate: () => {
              page.setAttribute('aria-expanded', 'true');
              document.body.setAttribute('data-frozen-scroll', 'true');

              if (header) {
                header.setAttribute('data-scheme', scheme);
              }

              const elements = page.querySelectorAll(
                ':scope > div > * > li, :scope > div > ul > animated-details > li, :scope > div > .section-header-tray-image-container .section-header-menu-image-container',
              );

              window.motion.animate(
                elements,
                {
                  opacity: [0, 1],
                  y: ['5px', 0],
                },
                {
                  delay: window.motion.stagger(0.1),
                },
              );
            },
            onDeactivate: () => {
              page.setAttribute('aria-expanded', 'false');
            },
            onPostDeactivate: () => {
              if (!window.traps.store['header-tray'].active) {
                document.body.removeAttribute('data-frozen-scroll');

                if (header) {
                  header.removeAttribute('data-scheme');
                }

                window.traps.deactivate();
              }
            },
            initialFocus: initial ?? undefined,
            fallbackFocus: close ?? undefined,
            allowOutsideClick: (e) => {
              const target = e.target;

              if (target && target instanceof Element && target.closest('header a, header button')) {
                return true;
              }

              return false;
            },
          });
        });

        window.addEventListener(
          'resize',
          window.debounce(() => {
            if (window.traps.store['header-tray'].active && window.innerWidth >= 1024) {
              document.body.removeAttribute('data-frozen-scroll');
              window.traps.store['header-tray'].deactivate();

              if (header) {
                header.removeAttribute('data-scheme');
              }
            }
          }, 500),
          {
            passive: true,
          },
        );
      }
    },
  );
}

if (!customElements.get('header-dropdown-menu')) {
  customElements.define(
    'header-dropdown-menu',
    class HeaderTrayMenu extends HTMLElement {
      constructor() {
        super();

        this.opens = this.querySelectorAll('[data-dropdown-open]');
        /** @type {NavPage[]} */
        this.pages = [];
      }

      connectedCallback() {
        this.opens.forEach((open) => {
          const parent = open.parentElement;
          /** @type {NavPage | null | undefined} */
          const page = parent?.querySelector('[data-dropdown-page]');

          open.addEventListener('focus', () => {
            this.pages.forEach((page) => this.closePage(page));
          });

          if (page && parent) {
            this.pages.push(page);
            window.traps.create(page);

            open.addEventListener('focus', () => {
              this.openPage(page);
            });

            /** @type {ReturnType<typeof setTimeout> | null} */
            let timer = null;

            const delay = 250;

            parent.addEventListener('mouseenter', () => {
              if (timer) {
                clearTimeout(timer);
              }

              timer = setTimeout(() => {
                Object.values(window.traps.store).forEach((trap) => trap.deactivate());
                this.openPage(page);

                const elements = page.querySelectorAll(
                  '.section-header-dropdown-menu-item-list-item, .section-header-menu-image',
                );

                window.motion.animate(
                  elements,
                  {
                    opacity: [0, 1],
                    y: ['10px', 0],
                  },
                  {
                    delay: window.motion.stagger(0.1),
                  },
                );
              }, delay);
            });

            parent.addEventListener('mouseleave', () => {
              if (timer) {
                clearTimeout(timer);
              }

              timer = setTimeout(() => {
                this.closePage(page);
              }, delay);
            });
          }
        });

        this.addEventListener('keyup', (e) => {
          if (e.key === 'Escape') {
            this.pages.forEach((menu) => this.closePage(menu));
          }
        });

        this.addEventListener('focusout', (e) => {
          if (!this.contains(/** @type {Node} */ (e.relatedTarget))) {
            this.pages.forEach((page) => this.closePage(page));
          }
        });
      }

      /** @param {NavPage} menu - The menu to open */
      openPage(menu) {
        menu.setAttribute('aria-expanded', 'true');
      }

      /** @param {NavPage} menu - The menu to close */
      closePage(menu) {
        menu.setAttribute('aria-expanded', 'false');
      }
    },
  );
}

if (!customElements.get('header-search-menu')) {
  customElements.define(
    'header-search-menu',
    class HeaderSearchMenu extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLButtonElement | null} */
        this.toggle = document.querySelector('[data-focus-trap-toggle="search"]');
      }

      connectedCallback() {
        if (this.toggle) {
          window.traps.create(this, {
            initialFocus: 'input[type="search"]',
            onActivate: () => {
              if (window.innerWidth < 1024) {
                document.body.setAttribute('data-frozen-scroll', 'true');
              }

              this?.setAttribute('aria-expanded', 'true');
            },
            onDeactivate: () => {
              this?.setAttribute('aria-expanded', 'false');
              document.body.removeAttribute('data-frozen-scroll');
            },
            fallbackFocus: this.toggle,
            allowOutsideClick: (e) => {
              const target = e.target;

              if (
                target &&
                target instanceof Element &&
                (target.closest('header-global a, header-global button') || !target.closest('header-global'))
              ) {
                if (!target.closest('header-global')) {
                  window.traps.store['search'].deactivate();
                }

                return true;
              }

              return false;
            },
          });
        }
      }
    },
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form');

        /** @type {HTMLInputElement | null} */
        this.input = this.querySelector('input[type="search"]');

        /** @type {HTMLElement | null} */
        this.results = this.querySelector('[data-results-container]');

        /** @type {HTMLElement | null} */
        this.recent = this.querySelector('[data-recent-list]');
      }

      connectedCallback() {
        if (this.input) {
          this.input.addEventListener(
            'input',
            window.debounce(() => this.changeHandler(), 200),
          );
        }

        if (this.form) {
          this.form.addEventListener('submit', this.submitHandler.bind(this));
        }

        this.renderRecentSearches();
      }

      async changeHandler() {
        if (!this.input || !this.results) return;

        const term = this.input.value.trim();

        if (!term.length) {
          return this.clearResults();
        }

        const response = await fetch(`/search/suggest?q=${term}&section_id=section-predictive-search-results`);

        if (response.ok) {
          const data = await response.text();

          const html = new DOMParser().parseFromString(data, 'text/html');
          const results = html.querySelector('[data-predictive-search-results]');

          if (results && results instanceof HTMLElement) {
            return (this.results.innerHTML = results.innerHTML);
          }
        }

        this.clearResults();

        throw new Error(response.statusText);
      }

      /**
       * @param {Event} e - The form submit event
       */
      async submitHandler(e) {
        e.preventDefault();

        if (this.form) {
          const term = this.input?.value.trim();

          if (term && term.length > 0) {
            const storage = localStorage.getItem('search-terms');
            const store = storage !== null ? JSON.parse(storage) : [];

            if (!store.includes(term)) {
              if (store.length > 5) {
                store.shift();
              }

              store.push(term);
            }

            localStorage.setItem('search-terms', JSON.stringify(store));

            this.form.submit();
          }
        }
      }

      clearResults() {
        if (this.results) this.results.innerHTML = '';
      }

      renderRecentSearches() {
        const storage = localStorage.getItem('search-terms');
        const terms = storage !== null ? JSON.parse(storage) : [];

        if (terms.length > 0 && this.recent) {
          const list = this.recent;

          terms.forEach((/** @type {string} */ term) => {
            const li = document.createElement('li');

            li.classList.add('predictive-search-recent-list-item');

            li.innerHTML = `<a href="/search?q=${term}">${term}</a>`;

            list.append(li);
          });
        }
      }
    },
  );
}
